import React, { useCallback, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  BarChartHorizontal,
  Users as UsersIcon,
  Users2,
  Building2,
  Gauge,
  Target,
  Briefcase,
} from 'lucide-react';
import SummaryStats from './SummaryStats';
import Panel from '../ui/Panel';
import Avatar from '../Avatar';
import InsightsDetailDrawer from './InsightsDetailDrawer';
import {
  displayName,
  utilizationStatusTextClass,
} from './utilizationHelpers';

/**
 * Panel with the standard "icon + title + optional subtitle + optional
 * trailing actions" header used across the Insights view. Keeps styling in
 * one place so every stats/chart card reads the same.
 */
const TitledPanel = ({
  icon: Icon,
  title,
  subtitle,
  actions,
  children,
  'data-testid': dataTestId,
}) => (
  <Panel data-testid={dataTestId}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h4 className="inline-flex items-center gap-2 text-sm font-semibold text-secondary-800 dark:text-secondary-100">
          {Icon && <Icon className="h-4 w-4 text-secondary-500 dark:text-secondary-400" />}
          <span className="truncate">{title}</span>
        </h4>
        {subtitle && (
          <p className="mt-0.5 text-xs text-secondary-500 dark:text-secondary-400">
            {subtitle}
          </p>
        )}
      </div>
      {actions}
    </div>
    {children}
  </Panel>
);

const UTILIZATION_BUCKETS = [
  {
    id: 'under_25',
    label: '0-25%',
    min: 0,
    max: 25,
    tone: 'bg-red-500',
  },
  {
    id: '25_50',
    label: '25-50%',
    min: 25,
    max: 50,
    tone: 'bg-amber-500',
  },
  {
    id: '50_75',
    label: '50-75%',
    min: 50,
    max: 75,
    tone: 'bg-yellow-500',
  },
  {
    id: '75_100',
    label: '75-100%',
    min: 75,
    max: 100,
    tone: 'bg-emerald-500',
  },
  {
    id: 'over_100',
    label: '>100%',
    min: 100,
    max: Infinity,
    tone: 'bg-primary-600',
  },
];

/** Bin users by their current utilization percent for a quick distribution view. */
const bucketUsers = (users = []) => {
  const counts = Object.fromEntries(
    UTILIZATION_BUCKETS.map((b) => [b.id, 0]),
  );
  users.forEach((u) => {
    const pct = Number(u?.utilization?.percent || 0);
    const bucket = UTILIZATION_BUCKETS.find(
      (b) => pct >= b.min && (b.id === 'over_100' ? pct > 100 : pct < b.max),
    );
    // Edge case: exactly 100% belongs in the >100% bucket when below max,
    // otherwise it maps to 75-100% - preserve that intent explicitly here.
    const key = bucket ? bucket.id : pct > 100 ? 'over_100' : '75_100';
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

const UtilizationDistribution = ({ users, onDrillDown }) => {
  const counts = useMemo(() => bucketUsers(users), [users]);
  const total = users.length || 1;
  // Pre-bucket users so each row click can hand the exact subset to the drawer
  // without re-filtering on every render.
  const bucketUsersByBand = useMemo(() => {
    const map = Object.fromEntries(UTILIZATION_BUCKETS.map((b) => [b.id, []]));
    users.forEach((u) => {
      const pct = Number(u?.utilization?.percent || 0);
      const bucket = UTILIZATION_BUCKETS.find(
        (b) => pct >= b.min && (b.id === 'over_100' ? pct > 100 : pct < b.max),
      );
      const key = bucket ? bucket.id : pct > 100 ? 'over_100' : '75_100';
      map[key].push(u);
    });
    return map;
  }, [users]);
  return (
    <TitledPanel
      icon={BarChart3}
      title="Utilization distribution"
      subtitle={`How ${users.length} employee${users.length === 1 ? '' : 's'} currently split across utilization bands.`}
      data-testid="insights-utilization-distribution"
    >
      <div className="space-y-2">
        {UTILIZATION_BUCKETS.map((b) => {
          const n = counts[b.id] || 0;
          const pct = Math.round((n / total) * 100);
          const clickable = typeof onDrillDown === 'function' && n > 0;
          return (
            <button
              key={b.id}
              type="button"
              onClick={
                clickable
                  ? () =>
                      onDrillDown({
                        title: `Utilization: ${b.label}`,
                        subtitle: `${n} ${n === 1 ? 'employee' : 'employees'} · ${pct}% of ${users.length}`,
                        users: bucketUsersByBand[b.id] || [],
                        metric: 'utilization',
                        icon: <BarChart3 className="h-5 w-5" />,
                      })
                  : undefined
              }
              disabled={!clickable}
              aria-label={`${b.label} band: ${n} employees`}
              className={`w-full flex items-center gap-3 text-sm rounded-md px-2 py-1 text-left transition-colors ${
                clickable
                  ? 'cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500'
                  : 'cursor-default'
              }`}
              data-testid={`distribution-row-${b.id}`}
            >
              <div className="w-16 text-secondary-600 dark:text-secondary-300 tabular-nums">
                {b.label}
              </div>
              <div className="flex-1 h-2 bg-secondary-100 dark:bg-secondary-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${b.tone} transition-all duration-300`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="w-16 text-right text-secondary-700 dark:text-secondary-200 font-medium tabular-nums">
                {n} <span className="text-secondary-400 dark:text-secondary-500 text-xs">({pct}%)</span>
              </div>
            </button>
          );
        })}
      </div>
    </TitledPanel>
  );
};

const BillabilitySplit = ({ users, onDrillDown }) => {
  const totals = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;
    const billableUsers = [];
    const nonBillableUsers = [];
    users.forEach((u) => {
      // Range-aware weekly billable fields so the split tracks the active
      // date filter instead of lifetime billing.
      const b = Number(u.billable_week_hours || 0);
      const nb = Number(u.non_billable_week_hours || 0);
      billable += b;
      nonBillable += nb;
      if (b > 0) billableUsers.push(u);
      if (nb > 0) nonBillableUsers.push(u);
    });
    const total = billable + nonBillable;
    return { billable, nonBillable, total, billableUsers, nonBillableUsers };
  }, [users]);
  const billPct = totals.total > 0 ? (totals.billable / totals.total) * 100 : 0;
  const nonBillPct = totals.total > 0 ? (totals.nonBillable / totals.total) * 100 : 0;
  const makeClick = (segment) => {
    if (typeof onDrillDown !== 'function') return undefined;
    const isBillable = segment === 'billable';
    const list = isBillable ? totals.billableUsers : totals.nonBillableUsers;
    if (!list.length) return undefined;
    return () =>
      onDrillDown({
          title: isBillable ? 'Billable contributors' : 'Non-billable contributors',
        subtitle: `${list.length} ${list.length === 1 ? 'employee' : 'employees'} · ${(isBillable ? totals.billable : totals.nonBillable).toFixed(1)}h`,
        users: list,
        metric: isBillable ? 'billable_week_hours' : 'non_billable_week_hours',
        icon: <DollarSign className="h-5 w-5" />,
      });
  };
  const onBillable = makeClick('billable');
  const onNonBillable = makeClick('non_billable');
  return (
    <TitledPanel
      icon={DollarSign}
      title="Billable vs non-billable"
      subtitle="Tracked hours split across the filtered employees."
      data-testid="insights-billability-split"
    >
      {totals.total === 0 ? (
        <div className="text-sm text-secondary-500 dark:text-secondary-400">
          No tracked hours in the current range.
        </div>
      ) : (
        <>
          <div className="h-3 w-full rounded-full overflow-hidden bg-secondary-100 dark:bg-secondary-700 flex">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${billPct}%` }}
              title={`${totals.billable.toFixed(1)}h billable`}
            />
            <div
              className="h-full bg-secondary-400 dark:bg-secondary-500 transition-all duration-300"
              style={{ width: `${nonBillPct}%` }}
              title={`${totals.nonBillable.toFixed(1)}h non-billable`}
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <button
              type="button"
              onClick={onBillable}
              disabled={!onBillable}
              data-testid="billability-row-billable"
              className={`flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors ${onBillable ? 'cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500' : 'cursor-default'}`}
            >
              <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />
              <span className="text-secondary-700 dark:text-secondary-200">
                Billable
              </span>
              <span className="ml-auto tabular-nums font-medium text-secondary-900 dark:text-white">
                {totals.billable.toFixed(1)}h
                <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-1">
                  ({Math.round(billPct)}%)
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={onNonBillable}
              disabled={!onNonBillable}
              data-testid="billability-row-non_billable"
              className={`flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors ${onNonBillable ? 'cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500' : 'cursor-default'}`}
            >
              <span className="inline-block w-3 h-3 rounded-sm bg-secondary-400 dark:bg-secondary-500" />
              <span className="text-secondary-700 dark:text-secondary-200">
                Non-billable
              </span>
              <span className="ml-auto tabular-nums font-medium text-secondary-900 dark:text-white">
                {totals.nonBillable.toFixed(1)}h
                <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-1">
                  ({Math.round(nonBillPct)}%)
                </span>
              </span>
            </button>
          </div>
        </>
      )}
    </TitledPanel>
  );
};

const TopEmployees = ({ users, mode = 'top', onDrillDown }) => {
  const sorted = useMemo(() => {
    const copy = [...users]
      .filter((u) => Number(u?.utilization?.percent || 0) > 0 || mode === 'bottom')
      .sort((a, b) => {
        const ap = Number(a?.utilization?.percent || 0);
        const bp = Number(b?.utilization?.percent || 0);
        return mode === 'top' ? bp - ap : ap - bp;
      });
    return copy.slice(0, 10);
  }, [users, mode]);
  const Icon = mode === 'top' ? TrendingUp : TrendingDown;
  const title = mode === 'top' ? 'Top 10 most utilized' : 'Top 10 least utilized';
  const clickable = typeof onDrillDown === 'function';
  return (
    <TitledPanel
      icon={Icon}
      title={title}
      subtitle={
        mode === 'top'
          ? 'Highest utilization across the filtered employees.'
          : 'Lowest utilization — potential capacity to pick up more work.'
      }
      data-testid={`insights-top-${mode}`}
    >
      {sorted.length === 0 ? (
        <div className="text-sm text-secondary-500 dark:text-secondary-400">
          No employees match the current filters.
        </div>
      ) : (
        <ul className="divide-y divide-secondary-100 dark:divide-secondary-800">
          {sorted.map((user) => {
            const util = user.utilization || {};
            const pct = Number(util.percent || 0);
            const barWidth = Math.max(0, Math.min(100, pct));
            return (
              <li key={user.id}>
                <button
                  type="button"
                  data-testid={`top-row-${mode}-${user.id}`}
                  onClick={
                    clickable
                      ? () =>
                          onDrillDown({
                            title: displayName(user),
                            subtitle: [user.department, user.cohort, user.team_lead]
                              .filter((v) => v && String(v).trim() !== '')
                              .join(' · ') || 'Employee details',
                            users: [user],
                            metric: 'utilization',
                            icon: Icon ? <Icon className="h-5 w-5" /> : null,
                          })
                      : undefined
                  }
                  disabled={!clickable}
                  className={`w-full py-2 flex items-center gap-3 text-left rounded-md px-2 transition-colors ${
                    clickable
                      ? 'cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500'
                      : 'cursor-default'
                  }`}
                >
                  <Avatar
                    blobName={user.avatar}
                    alt={displayName(user)}
                    size="sm"
                    fallbackText={displayName(user)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-secondary-900 dark:text-white truncate">
                      {displayName(user)}
                    </div>
                    <div className="mt-1 h-1.5 w-full bg-secondary-100 dark:bg-secondary-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${mode === 'top' ? 'bg-primary-600' : 'bg-amber-500'} transition-all duration-300`}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                  <div
                    className={`text-sm font-semibold tabular-nums ${utilizationStatusTextClass(util.status)}`}
                  >
                    {pct}%
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </TitledPanel>
  );
};

// ---------------------------------------------------------------------------
// Shared collapsible wrapper – used by every "breakdown" card so the chevron,
// localStorage persistence, and header shape stay consistent across cohort,
// department, team lead, pace, etc. We keep this inside InsightsView rather
// than extracting it into ../ui so the collapse semantics stay owned by the
// Insights surface (other views don't need them).
// ---------------------------------------------------------------------------
const useCollapsiblePref = (storageKey, defaultOpen = true) => {
  const [expanded, setExpanded] = React.useState(() => {
    if (!storageKey) return defaultOpen;
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved === null) return defaultOpen;
      return saved === 'true';
    } catch {
      return defaultOpen;
    }
  });
  React.useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, String(expanded));
    } catch {
      // storage can be denied in private mode / sandboxed iframes - ignore.
    }
  }, [expanded, storageKey]);
  return [expanded, setExpanded];
};

const CollapseToggle = ({ expanded, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex items-center gap-1 text-xs text-secondary-500 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200"
    aria-expanded={expanded}
  >
    {expanded ? (
      <>
        Collapse <ChevronUp className="h-3.5 w-3.5" />
      </>
    ) : (
      <>
        Expand <ChevronDown className="h-3.5 w-3.5" />
      </>
    )}
  </button>
);

/**
 * Palette for the avg-utilization bar inside each breakdown card. Mirrors the
 * utilization bands used elsewhere so a cohort that averages 85% reads as
 * "healthy" (emerald) without the user having to decode a legend.
 */
const utilBandClass = (pct) => {
  if (pct >= 100) return 'bg-primary-600';
  if (pct >= 75) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-yellow-500';
  if (pct >= 25) return 'bg-amber-500';
  return 'bg-red-500';
};

/**
 * Generic "group users by an attribute" card. Each tile shows the group's
 * employee count plus the group's average utilization as a mini bar so the
 * card doubles as a quick load-balance read, not just a headcount roll-up.
 *
 * Keyed off a `fieldKey` so it works for cohort, department, location, etc.
 */
const GroupBreakdown = ({
  users = [],
  fieldKey,
  title,
  subtitle,
  icon,
  emptyLabel = 'Unassigned',
  storageKey,
  testId,
  onDrillDown,
  drillInLabel = 'in',
}) => {
  const groups = useMemo(() => {
    const byKey = new Map();
    users.forEach((u) => {
      const raw = u?.[fieldKey];
      const name =
        raw == null || String(raw).trim() === '' ? emptyLabel : String(raw);
      const bucket = byKey.get(name) || {
        name,
        count: 0,
        utilSum: 0,
        utilCount: 0,
        // Keep the actual users on the bucket so a click can hand the full
        // list to the drill-in drawer without re-scanning the dataset.
        users: [],
      };
      bucket.count += 1;
      const pct = Number(u?.utilization?.percent || 0);
      bucket.utilSum += pct;
      bucket.utilCount += 1;
      bucket.users.push(u);
      byKey.set(name, bucket);
    });
    return [...byKey.values()]
      .map((g) => ({
        ...g,
        avg: g.utilCount > 0 ? g.utilSum / g.utilCount : 0,
      }))
      // Largest group first; "Unassigned" sinks to the bottom when tied so the
      // view surfaces real data before the catch-all bucket.
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        if (a.name === emptyLabel) return 1;
        if (b.name === emptyLabel) return -1;
        return a.name.localeCompare(b.name);
      });
  }, [users, fieldKey, emptyLabel]);
  const [expanded, setExpanded] = useCollapsiblePref(storageKey);

  if (!groups.length) return null;
  const clickable = typeof onDrillDown === 'function';
  return (
    <TitledPanel
      icon={icon}
      title={title}
      subtitle={subtitle}
      data-testid={testId}
      actions={
        <CollapseToggle
          expanded={expanded}
          onToggle={() => setExpanded((p) => !p)}
        />
      }
    >
      {expanded && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {groups.map((g) => {
            const avg = Math.round(g.avg);
            const barWidth = Math.max(0, Math.min(100, g.avg));
            const Element = clickable ? 'button' : 'div';
            return (
              <Element
                key={g.name}
                type={clickable ? 'button' : undefined}
                onClick={
                  clickable
                    ? () =>
                        onDrillDown({
                          title: `Employees ${drillInLabel}: ${g.name}`,
                          subtitle: `${g.count} ${g.count === 1 ? 'employee' : 'employees'} · ${avg}% avg utilization`,
                          users: g.users,
                          metric: 'utilization',
                          icon: icon ? React.createElement(icon, { className: 'h-5 w-5' }) : null,
                        })
                    : undefined
                }
                className={`text-left border border-secondary-200 dark:border-secondary-700 rounded-lg p-3 bg-secondary-50 dark:bg-secondary-900/40 ${
                  clickable
                    ? 'cursor-pointer hover:bg-primary-50 hover:border-primary-300 dark:hover:bg-primary-900/30 dark:hover:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-secondary-800 transition'
                    : ''
                }`}
                aria-label={clickable ? `View ${g.count} employees in ${g.name}` : undefined}
                data-testid={`${testId}-item-${g.name}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-xl font-bold text-primary-600 dark:text-primary-400 tabular-nums">
                    {g.count}
                  </div>
                  <div className="text-xs text-secondary-500 dark:text-secondary-400 tabular-nums">
                    {avg}% avg
                  </div>
                </div>
                <div
                  className="text-sm text-secondary-700 dark:text-secondary-300 truncate mt-0.5"
                  title={g.name}
                >
                  {g.name}
                </div>
                <div className="mt-2 h-1.5 w-full bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${utilBandClass(g.avg)} transition-all duration-300`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </Element>
            );
          })}
        </div>
      )}
    </TitledPanel>
  );
};

/**
 * Buckets users by their current pace label (ahead / on_pace / behind) and
 * renders a stacked bar + legend. Pulls from `utilization.pace_label` set by
 * the backend's pro-rated pacing logic. Users without a label (e.g. non-pace
 * ranges) fall into a neutral "No pace data" bucket so counts always add up.
 */
const PACE_BANDS = [
  { id: 'ahead', label: 'Ahead', tone: 'bg-primary-600', text: 'text-primary-600 dark:text-primary-400' },
  { id: 'on_pace', label: 'On pace', tone: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  { id: 'behind', label: 'Behind pace', tone: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
  { id: 'none', label: 'No pace data', tone: 'bg-secondary-400 dark:bg-secondary-500', text: 'text-secondary-500 dark:text-secondary-400' },
];

const PaceDistribution = ({ users, onDrillDown }) => {
  const usersByBand = useMemo(() => {
    const map = Object.fromEntries(PACE_BANDS.map((b) => [b.id, []]));
    users.forEach((u) => {
      const label = u?.utilization?.pace_label;
      const key = label && map[label] !== undefined ? label : 'none';
      map[key].push(u);
    });
    return map;
  }, [users]);
  const total = users.length || 1;
  // Hide the "no pace data" segment when every user has a real label – avoids
  // a confusing empty-labeled slice. Keep the band in the legend though.
  const anyUnlabeled = (usersByBand.none || []).length > 0;
  return (
    <TitledPanel
      icon={Gauge}
      title="Pace this period"
      subtitle="How many employees are tracking ahead, on pace, or behind their pro-rated target."
      data-testid="insights-pace-distribution"
    >
      <div className="h-3 w-full rounded-full overflow-hidden bg-secondary-100 dark:bg-secondary-700 flex">
        {PACE_BANDS.map((b) => {
          const n = (usersByBand[b.id] || []).length;
          const pct = (n / total) * 100;
          if (!n) return null;
          return (
            <div
              key={b.id}
              className={`h-full ${b.tone} transition-all duration-300`}
              style={{ width: `${pct}%` }}
              title={`${n} ${b.label}`}
              data-testid={`pace-segment-${b.id}`}
            />
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {PACE_BANDS.filter((b) => b.id !== 'none' || anyUnlabeled).map((b) => {
          const n = (usersByBand[b.id] || []).length;
          const pct = Math.round((n / total) * 100);
          const clickable = typeof onDrillDown === 'function' && n > 0;
          return (
            <button
              key={b.id}
              type="button"
              onClick={
                clickable
                  ? () =>
                      onDrillDown({
                        title: `Pace: ${b.label}`,
                        subtitle: `${n} ${n === 1 ? 'employee' : 'employees'} · ${pct}% of ${users.length}`,
                        users: usersByBand[b.id] || [],
                        metric: 'utilization',
                        icon: <Gauge className="h-5 w-5" />,
                      })
                  : undefined
              }
              disabled={!clickable}
              aria-label={`${b.label}: ${n} employees`}
              className={`flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors ${
                clickable
                  ? 'cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500'
                  : 'cursor-default'
              }`}
              data-testid={`pace-row-${b.id}`}
            >
              <span className={`inline-block w-3 h-3 rounded-sm ${b.tone}`} />
              <span className="text-secondary-700 dark:text-secondary-200">
                {b.label}
              </span>
              <span
                className={`ml-auto tabular-nums font-medium ${b.text}`}
              >
                {n}
                <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-1">
                  ({pct}%)
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </TitledPanel>
  );
};

/**
 * Aggregates `per_project` rows across all filtered users to show the top
 * projects consuming time this period. Each project row is a horizontal bar
 * so you can eyeball concentration without reading numbers. Zero-hour
 * projects (empty allocations without tracked time) are filtered out — they
 * clutter the chart without conveying anything.
 */
const TopProjectsByHours = ({ users, limit = 10, onDrillDown }) => {
  const rows = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      (u.per_project || []).forEach((p) => {
        if (!p?.project_id) return;
        const key = String(p.project_id);
        const existing = map.get(key) || {
          project_id: key,
          project_name: p.project_name || 'Project',
          is_billable: !!p.is_billable,
          tracked_hours: 0,
          // contributors keeps the actual user list so clicking the row can
          // open the drawer against the right subset without re-aggregating.
          contributors: [],
        };
        // `per_project.week_hours` is the range-aware figure the backend sums
        // to utilization.week_hours; `tracked_hours` on the same object is
        // lifetime, so using it here made the bars reflect historical totals
        // instead of the active filter.
        const projectHours = Number(p.week_hours || 0);
        existing.tracked_hours += projectHours;
        if (projectHours > 0) {
          existing.contributors.push({ user: u, hours: projectHours });
        }
        map.set(key, existing);
      });
    });
    return [...map.values()]
      // Threshold matches the one-decimal display: anything that would round
      // to "0.0h" is noise rather than signal and clutters the chart.
      .filter((p) => p.tracked_hours >= 0.1)
      .sort((a, b) => b.tracked_hours - a.tracked_hours)
      .slice(0, limit);
  }, [users, limit]);

  const maxHours = rows.length ? rows[0].tracked_hours : 0;

  return (
    <TitledPanel
      icon={BarChartHorizontal}
      title={`Top ${limit} projects by tracked hours`}
      subtitle="Where the team's time landed across the current filters."
      data-testid="insights-top-projects"
    >
      {rows.length === 0 ? (
        <div className="text-sm text-secondary-500 dark:text-secondary-400">
          No tracked hours against any project in the current range.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((p) => {
            const widthPct = maxHours > 0 ? (p.tracked_hours / maxHours) * 100 : 0;
            const clickable = typeof onDrillDown === 'function';
            // Each contributor row needs the *project-specific* hours, not the
            // user's weekly total, so we attach a per-user override that the
            // drawer picks up through `getRowValue`.
            const contributorHours = new Map(
              p.contributors.map((c) => [c.user.id, c.hours]),
            );
            const contributorUsers = p.contributors.map((c) => c.user);
            return (
              <li key={p.project_id}>
                <button
                  type="button"
                  data-testid={`insights-top-project-${p.project_id}`}
                  onClick={
                    clickable
                      ? () =>
                          onDrillDown({
                            title: p.project_name,
                            subtitle: `${p.tracked_hours.toFixed(1)}h tracked by ${contributorUsers.length} ${contributorUsers.length === 1 ? 'person' : 'people'}`,
                            users: contributorUsers,
                            metric: 'tracked_hours',
                            icon: <Briefcase className="h-5 w-5" />,
                            getRowValue: (u) => {
                              const h = contributorHours.get(u.id) || 0;
                              return {
                                value: `${h.toFixed(1)}h`,
                                label: 'on project',
                              };
                            },
                            getSortScore: (u) => contributorHours.get(u.id) || 0,
                          })
                      : undefined
                  }
                  disabled={!clickable}
                  aria-label={clickable ? `View ${contributorUsers.length} contributors to ${p.project_name}` : undefined}
                  className={`w-full grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-center rounded-md px-2 py-1 text-left transition-colors ${
                    clickable
                      ? 'cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500'
                      : 'cursor-default'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm text-secondary-800 dark:text-secondary-100">
                      <span className="truncate" title={p.project_name}>
                        {p.project_name}
                      </span>
                      {p.is_billable ? (
                        <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          Billable
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-300">
                          Non-billable
                        </span>
                      )}
                    </div>
                    <div className="mt-1 h-1.5 w-full bg-secondary-100 dark:bg-secondary-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${p.is_billable ? 'bg-emerald-500' : 'bg-secondary-400 dark:bg-secondary-500'} transition-all duration-300`}
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-secondary-900 dark:text-white tabular-nums">
                    {p.tracked_hours.toFixed(1)}h
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </TitledPanel>
  );
};

/**
 * Capacity vs tracked overview — surfaces the aggregate pulse of the team:
 * total planned capacity (sum of per-user weekly targets, falling back to
 * 40h/person when the backend doesn't send one), total tracked, the gap,
 * and realised billable %.
 */
const CapacityOverview = ({ users, onDrillDown, rangeLabel }) => {
  const totals = useMemo(() => {
    let capacity = 0;
    let tracked = 0;
    let billable = 0;
    let targeted = 0;
    users.forEach((u) => {
      const util = u.utilization || {};
      // Prefer `target_hours` because the backend already scales it to the
      // selected range (pace-to-date for this_week, full range target
      // otherwise). `weekly_target_hours` is a raw 40h and only matches when
      // the range is exactly one week, so using it unconditionally made the
      // tile wildly misleading on month / multi-month ranges.
      const perUserCapacity = Number(
        util.target_hours != null
          ? util.target_hours
          : util.weekly_target_hours != null
            ? util.weekly_target_hours
            : 40,
      );
      capacity += perUserCapacity;
      targeted += perUserCapacity > 0 ? 1 : 0;
      // CRITICAL: `user.tracked_hours` / `billable_hours` are all-time totals;
      // only `utilization.week_hours` + `billable_week_hours` are scoped to
      // the active date range. Using the all-time values here produced a
      // 2000%+ "utilization" on this_week because tracked summed to lifetime.
      tracked += Number(util.week_hours || 0);
      billable += Number(u.billable_week_hours || 0);
    });
    return {
      capacity,
      tracked,
      billable,
      gap: capacity - tracked,
      billableRatio: tracked > 0 ? (billable / tracked) * 100 : 0,
      utilization: capacity > 0 ? (tracked / capacity) * 100 : 0,
      people: targeted,
    };
  }, [users]);

  const utilPct = Math.round(totals.utilization);
  const barWidth = Math.max(0, Math.min(100, totals.utilization));

  // Each tile dispatches the exact slice of users relevant to its metric so
  // the drawer stays concise instead of dumping the whole list each time.
  const makeTileClick = (payload) =>
    typeof onDrillDown === 'function' && payload.users.length
      ? () => onDrillDown(payload)
      : undefined;

  // Everything here is scoped to the active date range – pick the matching
  // range-aware fields (week_hours / billable_week_hours), not the all-time
  // ones on the user root.
  const rangeTracked = (u) => Number(u?.utilization?.week_hours || 0);
  const rangeBillable = (u) => Number(u?.billable_week_hours || 0);
  const rangeTarget = (u) => {
    const util = u?.utilization || {};
    return Number(
      util.target_hours != null
        ? util.target_hours
        : util.weekly_target_hours != null
          ? util.weekly_target_hours
          : 40,
    );
  };

  const billableUsers = users.filter((u) => rangeBillable(u) > 0);
  const trackedUsers = users.filter((u) => rangeTracked(u) > 0);
  const overUsers = users.filter((u) => rangeTracked(u) > rangeTarget(u));
  const underUsers = users.filter((u) => rangeTracked(u) <= rangeTarget(u));

  const onCapacity = makeTileClick({
    title: 'Capacity by person',
    subtitle: `${users.length} ${users.length === 1 ? 'employee' : 'employees'} · ${totals.capacity.toFixed(0)}h total`,
    users,
    metric: 'remaining',
    icon: <Target className="h-5 w-5" />,
  });
  const onTracked = makeTileClick({
    title: 'Tracked hours by person',
    subtitle: `${trackedUsers.length} ${trackedUsers.length === 1 ? 'person' : 'people'} · ${totals.tracked.toFixed(1)}h total`,
    users: trackedUsers,
    metric: 'week_hours',
    icon: <Target className="h-5 w-5" />,
  });
  const onGap = makeTileClick({
    title: totals.gap >= 0 ? 'Headroom by person' : 'Over capacity',
    subtitle:
      totals.gap >= 0
        ? `${Math.abs(totals.gap).toFixed(1)}h of headroom`
        : `${Math.abs(totals.gap).toFixed(1)}h beyond planned`,
    users: totals.gap >= 0 ? underUsers : overUsers,
    metric: 'remaining',
    icon: <Target className="h-5 w-5" />,
  });
  const onBillable = makeTileClick({
    title: 'Billable contributors',
    subtitle: `${billableUsers.length} ${billableUsers.length === 1 ? 'person' : 'people'} · ${totals.billable.toFixed(1)}h billable`,
    users: billableUsers,
    metric: 'billable_week_hours',
    icon: <DollarSign className="h-5 w-5" />,
  });

  const tileClass = (click) =>
    `w-full text-left rounded-lg bg-secondary-50 dark:bg-secondary-900/40 border border-secondary-200 dark:border-secondary-700 p-3 ${
      click
        ? 'cursor-pointer hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50/40 dark:hover:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-primary-500 transition'
        : ''
    }`;

  const rangeSuffix = rangeLabel ? ` · ${rangeLabel}` : '';
  return (
    <TitledPanel
      icon={Target}
      title="Team capacity vs tracked"
      subtitle={`Rolled-up capacity vs actual tracked hours for the selected range${rangeSuffix}.`}
      data-testid="insights-capacity-overview"
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <button
          type="button"
          onClick={onCapacity}
          disabled={!onCapacity}
          className={tileClass(onCapacity)}
          data-testid="capacity-tile-capacity"
        >
          <div className="text-[11px] uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
            Capacity
          </div>
          <div className="mt-0.5 text-xl font-bold text-secondary-900 dark:text-white tabular-nums">
            {totals.capacity.toFixed(0)}h
          </div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400">
            Across {totals.people} people
          </div>
        </button>
        <button
          type="button"
          onClick={onTracked}
          disabled={!onTracked}
          className={tileClass(onTracked)}
          data-testid="capacity-tile-tracked"
        >
          <div className="text-[11px] uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
            Tracked
          </div>
          <div className="mt-0.5 text-xl font-bold text-secondary-900 dark:text-white tabular-nums">
            {totals.tracked.toFixed(1)}h
          </div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400">
            {utilPct}% of capacity
          </div>
        </button>
        <button
          type="button"
          onClick={onGap}
          disabled={!onGap}
          className={tileClass(onGap)}
          data-testid="capacity-tile-gap"
        >
          <div className="text-[11px] uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
            {totals.gap >= 0 ? 'Remaining' : 'Over capacity'}
          </div>
          <div
            className={`mt-0.5 text-xl font-bold tabular-nums ${totals.gap >= 0 ? 'text-secondary-900 dark:text-white' : 'text-red-600 dark:text-red-400'}`}
          >
            {Math.abs(totals.gap).toFixed(1)}h
          </div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400">
            {totals.gap >= 0 ? 'headroom this week' : 'beyond planned'}
          </div>
        </button>
        <button
          type="button"
          onClick={onBillable}
          disabled={!onBillable}
          className={tileClass(onBillable)}
          data-testid="capacity-tile-billable"
        >
          <div className="text-[11px] uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
            Billable share
          </div>
          <div className="mt-0.5 text-xl font-bold text-secondary-900 dark:text-white tabular-nums">
            {Math.round(totals.billableRatio)}%
          </div>
          <div className="text-[11px] text-secondary-500 dark:text-secondary-400">
            {totals.billable.toFixed(1)}h of {totals.tracked.toFixed(1)}h
          </div>
        </button>
      </div>
      <div className="h-2 w-full bg-secondary-100 dark:bg-secondary-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${utilBandClass(totals.utilization)} transition-all duration-500`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-secondary-500 dark:text-secondary-400">
        <span>0h</span>
        <span>{utilPct}% utilised</span>
        <span>{totals.capacity.toFixed(0)}h</span>
      </div>
    </TitledPanel>
  );
};

const TeamLeadBreakdown = ({
  teamLeadBreakdown = [],
  onOpenTeamLead,
  storageKey,
}) => {
  const [expanded, setExpanded] = useCollapsiblePref(storageKey);
  if (!teamLeadBreakdown.length) return null;
  return (
    <TitledPanel
      icon={UsersIcon}
      title="By team lead"
      subtitle="How many filtered employees roll up to each lead."
      data-testid="insights-team-lead-breakdown"
      actions={
        <CollapseToggle
          expanded={expanded}
          onToggle={() => setExpanded((p) => !p)}
        />
      }
    >
      {expanded && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {teamLeadBreakdown.map((group) => (
            <button
              key={group.lead}
              type="button"
              onClick={() => onOpenTeamLead?.(group)}
              className="text-left border border-secondary-200 dark:border-secondary-700 rounded-lg p-3 bg-secondary-50 hover:bg-primary-50 hover:border-primary-300 dark:bg-secondary-900/40 dark:hover:bg-primary-900/30 dark:hover:border-primary-600 transition focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-secondary-800"
              title={`View ${group.count} resource${group.count === 1 ? '' : 's'} under ${group.lead}`}
              data-testid={`insights-team-lead-${group.lead}`}
            >
              <div className="text-xl font-bold text-primary-600 dark:text-primary-400">
                {group.count}
              </div>
              <div
                className="text-sm text-secondary-700 dark:text-secondary-300 truncate mt-0.5"
                title={group.lead}
              >
                {group.lead}
              </div>
            </button>
          ))}
        </div>
      )}
    </TitledPanel>
  );
};

/**
 * Insights sub-view: aggregated stats + charts separated from the dense
 * People / Projects / Timeline tables so the main surfaces stay focused.
 */
const InsightsView = ({
  users = [],
  teamLeadBreakdown = [],
  onOpenTeamLead,
  rangeLabel = 'this week',
  teamLeadStorageKey,
}) => {
  // Single drawer, driven by whichever tile was clicked. `null` = closed.
  const [detail, setDetail] = useState(null);
  const openDetail = useCallback((payload) => setDetail(payload || null), []);
  const closeDetail = useCallback(() => setDetail(null), []);

  // SummaryStats talks in card ids — translate each into the drawer payload
  // locally so SummaryStats stays dumb and reusable.
  const handleSummaryClick = useCallback(
    (id) => {
      const now = new Date();
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      switch (id) {
        case 'billable':
          openDetail({
            title: 'Employees with billable work',
            // Range-aware so clicking "With billable work: 30" shows the 30
            // who billed in the active filter, not everyone who ever billed.
            users: users.filter(
              (u) => Number(u.billable_week_hours || 0) > 0,
            ),
            metric: 'billable_week_hours',
            icon: <DollarSign className="h-5 w-5" />,
          });
          break;
        case 'overallocated':
          openDetail({
            title: 'Over-allocated employees',
            users: users.filter((u) => u.is_over_allocated),
            metric: 'utilization',
            icon: <UsersIcon className="h-5 w-5" />,
          });
          break;
        case 'free':
          openDetail({
            title: 'Employees free this month',
            users: users.filter((u) => {
              if (!u.free_date) return false;
              const fd = new Date(u.free_date);
              return !Number.isNaN(fd.getTime()) && fd >= now && fd <= monthEnd;
            }),
            metric: 'free_date',
            icon: <UsersIcon className="h-5 w-5" />,
          });
          break;
        case 'avg':
          openDetail({
            title: 'All employees by utilization',
            users,
            metric: 'utilization',
            icon: <UsersIcon className="h-5 w-5" />,
          });
          break;
        case 'total':
        default:
          openDetail({
            title: 'All matching employees',
            users,
            metric: 'utilization',
            icon: <UsersIcon className="h-5 w-5" />,
          });
      }
    },
    [users, openDetail],
  );

  if (!users.length) {
    return (
      <Panel>
        <div className="text-center py-10 text-secondary-500 dark:text-secondary-400">
          No employees match the current filters — adjust them above to see
          insights.
        </div>
      </Panel>
    );
  }

  const rangeLower = (rangeLabel || 'this week').toLowerCase();

  return (
    <div className="space-y-4" data-testid="resource-insights-view">
      <SummaryStats users={users} onCardClick={handleSummaryClick} />
      <CapacityOverview
        users={users}
        onDrillDown={openDetail}
        rangeLabel={rangeLabel}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UtilizationDistribution users={users} onDrillDown={openDetail} />
        <PaceDistribution users={users} onDrillDown={openDetail} />
      </div>
      <BillabilitySplit users={users} onDrillDown={openDetail} />
      <GroupBreakdown
        users={users}
        fieldKey="cohort"
        title="By cohort"
        subtitle="Where employees sit across cohorts, with each cohort's average utilization. Click any tile to see the people."
        icon={Users2}
        emptyLabel="No cohort"
        storageKey="resource_insights_cohort_expanded"
        testId="insights-cohort-breakdown"
        onDrillDown={openDetail}
        drillInLabel="in cohort"
      />
      <GroupBreakdown
        users={users}
        fieldKey="department"
        title="By department"
        subtitle="Headcount and average utilization per department. Click a tile to see members."
        icon={Building2}
        emptyLabel="No department"
        storageKey="resource_insights_dept_expanded"
        testId="insights-department-breakdown"
        onDrillDown={openDetail}
        drillInLabel="in"
      />
      <TeamLeadBreakdown
        teamLeadBreakdown={teamLeadBreakdown}
        onOpenTeamLead={onOpenTeamLead}
        storageKey={teamLeadStorageKey}
      />
      <TopProjectsByHours users={users} onDrillDown={openDetail} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopEmployees users={users} mode="top" onDrillDown={openDetail} />
        <TopEmployees users={users} mode="bottom" onDrillDown={openDetail} />
      </div>
      <div className="text-xs text-secondary-500 dark:text-secondary-400">
        Numbers reflect the current filters and the {rangeLower} range selected
        above. Click any tile to drill into the matching employees.
      </div>
      <InsightsDetailDrawer
        open={!!detail}
        onClose={closeDetail}
        title={detail?.title || ''}
        subtitle={detail?.subtitle}
        users={detail?.users || []}
        metric={detail?.metric}
        icon={detail?.icon}
        getRowValue={detail?.getRowValue}
        getSortScore={detail?.getSortScore}
      />
    </div>
  );
};

export default InsightsView;
